<div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
  <div class="flex items-center justify-between mb-4">
    <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notas internas</h2>
    <span class="text-xs text-gray-400 dark:text-gray-500">Solo visibles para tu equipo</span>
  </div>

  <!-- Composer de creación (oculto mientras se edita una nota existente) -->
  @if (!isEditing) {
    <div class="mb-5">
      <ng-container *ngTemplateOutlet="composer; context: { editing: false }"></ng-container>
    </div>
  }

  <!-- Carga -->
  @if (loading) {
    <div class="flex items-center gap-3 py-6 justify-center">
      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
      <span class="text-sm text-gray-500 dark:text-gray-400">Cargando notas...</span>
    </div>
  } @else if (loadError) {
    <div class="rounded-lg border border-error-200 bg-error-50 p-3 dark:border-error-500/20 dark:bg-error-500/10">
      <p class="text-sm text-error-600 dark:text-error-400">{{ loadError }}</p>
    </div>
  } @else if (notes.length === 0) {
    <div class="flex flex-col items-center gap-2 py-8 text-gray-400 dark:text-gray-500">
      <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
      </svg>
      <p class="text-sm">Aún no hay notas. Escribe la primera arriba.</p>
    </div>
  } @else {
    <ul class="space-y-4">
      @for (note of notes; track note.id) {
        <li class="flex gap-3">
          <!-- Avatar -->
          <div class="shrink-0 w-9 h-9 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 flex items-center justify-center text-xs font-semibold">
            {{ authorInitials(note) }}
          </div>

          <div class="flex-1 min-w-0">
            @if (editingNoteId === note.id) {
              <!-- Editor inline -->
              <ng-container *ngTemplateOutlet="composer; context: { editing: true }"></ng-container>
            } @else {
              <div class="rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-3.5 py-3">
                <div class="flex items-center justify-between gap-2 flex-wrap">
                  <div class="min-w-0">
                    <span class="text-sm font-medium text-gray-800 dark:text-white/90">{{ note.authorName }}</span>
                    <span class="ml-2 text-xs text-gray-400 dark:text-gray-500" [title]="formatDateTime(note.datetimeCreated)">
                      {{ relativeTime(note.datetimeCreated) }}
                      @if (note.datetimeUpdated && note.datetimeUpdated !== note.datetimeCreated) {
                        <span class="italic">· editada</span>
                      }
                    </span>
                  </div>
                  @if (isAuthor(note)) {
                    <div class="flex items-center gap-1 shrink-0">
                      <button type="button" (click)="startEdit(note)"
                        class="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Editar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                      <button type="button" (click)="eliminar(note)" [disabled]="isDeleting(note)"
                        class="p-1.5 rounded-lg text-gray-400 hover:text-error-600 dark:hover:text-error-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50" title="Eliminar">
                        @if (isDeleting(note)) {
                          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        } @else {
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        }
                      </button>
                    </div>
                  }
                </div>

                <!-- Contenido con menciones resaltadas -->
                <p class="mt-1.5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words" [innerHTML]="renderedContent(note)"></p>

                <!-- Adjuntos -->
                @if (noteAttachments(note).length > 0) {
                  <ul class="mt-2.5 space-y-1.5">
                    @for (att of noteAttachments(note); track att.storageRef) {
                      <li>
                        <a [href]="att.url" target="_blank" rel="noopener" [download]="att.fileName"
                          class="flex items-center gap-2.5 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                          <app-quote-attachment-icon [mimeType]="att.mimeType" [fileName]="att.fileName"></app-quote-attachment-icon>
                          <span class="flex-1 min-w-0 text-xs font-medium text-gray-700 dark:text-gray-300 truncate" [title]="att.fileName">{{ att.fileName }}</span>
                          @if (formatSize(att.sizeBytes)) {
                            <span class="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">{{ formatSize(att.sizeBytes) }}</span>
                          }
                          <svg class="shrink-0 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"/>
                          </svg>
                        </a>
                      </li>
                    }
                  </ul>
                }
              </div>
            }
          </div>
        </li>
      }
    </ul>
  }
</div>

<!-- ============ Composer reutilizable (crear / editar) ============ -->
<ng-template #composer let-editing="editing">
  <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-3">
    @if (editing) {
      <p class="text-xs font-medium text-brand-600 dark:text-brand-400 mb-2">Editando nota</p>
    }

    <!-- Textarea + dropdown de menciones -->
    <div class="relative">
      <textarea #composerTextarea rows="3"
        [value]="draftContent"
        (input)="onComposerInput()"
        (keydown)="onComposerKeydown($event)"
        (blur)="onComposerBlur()"
        placeholder="Escribe una nota... usa @ para mencionar a alguien de tu equipo."
        class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 resize-y"></textarea>

      @if (showMentionDropdown) {
        <ul class="absolute left-0 right-0 mt-1 z-dropdown max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1">
          @for (user of mentionResults; track user.id; let i = $index) {
            <li>
              <button type="button" (mousedown)="$event.preventDefault()" (click)="selectMention(user)"
                class="w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors"
                [class]="i === mentionActiveIndex ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'">
                <span class="shrink-0 w-7 h-7 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 flex items-center justify-center text-[11px] font-semibold">
                  {{ (user.fullname || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="min-w-0">
                  <span class="block text-sm text-gray-800 dark:text-white/90 truncate">{{ userName(user) }}</span>
                  <span class="block text-xs text-gray-400 dark:text-gray-500 truncate">{{ user.mail }}</span>
                </span>
              </button>
            </li>
          }
        </ul>
      }
    </div>

    <!-- Chips de menciones seleccionadas -->
    @if (draftMentions.length > 0) {
      <div class="mt-2 flex flex-wrap gap-1.5">
        @for (m of draftMentions; track m.id) {
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
            @{{ m.name }}
            <button type="button" (click)="removeMention(m.id)" class="hover:text-brand-900 dark:hover:text-brand-200">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </span>
        }
      </div>
    }

    <!-- Chips de adjuntos -->
    @if (draftAttachments.length > 0) {
      <ul class="mt-2 space-y-1.5">
        @for (att of draftAttachments; track att.storageRef; let i = $index) {
          <li class="flex items-center gap-2.5 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5">
            <app-quote-attachment-icon [mimeType]="att.mimeType" [fileName]="att.fileName"></app-quote-attachment-icon>
            <span class="flex-1 min-w-0 text-xs font-medium text-gray-700 dark:text-gray-300 truncate" [title]="att.fileName">{{ att.fileName }}</span>
            @if (formatSize(att.sizeBytes)) {
              <span class="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">{{ formatSize(att.sizeBytes) }}</span>
            }
            <button type="button" (click)="removeAttachment(i)" class="shrink-0 p-1 rounded text-gray-400 hover:text-error-600 dark:hover:text-error-400">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </li>
        }
      </ul>
    }

    <!-- Subida en curso -->
    @if (uploadingFile) {
      <div class="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-brand-500"></div>
        Subiendo {{ uploadingFileName }}...
      </div>
    }

    <!-- Barra de acciones -->
    <div class="mt-3 flex items-center justify-between gap-2 flex-wrap">
      <div class="flex items-center gap-3">
        <label class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
          [class.opacity-50]="uploadingFile" [class.pointer-events-none]="uploadingFile">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
          </svg>
          Adjuntar
          <input type="file" [accept]="accept" class="hidden" (change)="onFileSelected($event)">
        </label>
        <span class="text-[11px]" [class]="contentLength > 5000 ? 'text-error-500' : 'text-gray-400 dark:text-gray-500'">
          {{ contentLength }}/5000
        </span>
      </div>
      <div class="flex items-center gap-2">
        @if (editing) {
          <button type="button" (click)="cancelEdit()" [disabled]="saving"
            class="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
            Cancelar
          </button>
        }
        <button type="button" (click)="submit()" [disabled]="saving || uploadingFile"
          class="px-4 py-1.5 rounded-lg text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center gap-2">
          @if (saving) {
            <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
          }
          {{ editing ? 'Guardar cambios' : 'Agregar nota' }}
        </button>
      </div>
    </div>
  </div>
</ng-template>
